// Sprint 6 (Phase 5, D44) — TodoUpdatePill.
//
// Small inline pill rendered above a TodoWrite checklist when the new emit
// changes the status of one or more items. Pure projection; not persisted.

import { Icon, type IconName } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import type { TKey } from '../../i18n';
import type { TodoUpdatePill as Pill } from '../../lib/tool-rendering/diff-todos';

export interface TodoUpdatePillsProps {
  pills: Pill[];
}

export function TodoUpdatePills({ pills }: TodoUpdatePillsProps) {
  const { t } = useTranslation();
  if (pills.length === 0) return null;
  return (
    <div
      className="flex flex-wrap gap-1.5 mb-2"
      data-testid="todo-update-pills"
    >
      {pills.map((p) => (
        <TodoUpdatePillItem key={p.key} pill={p} t={t} />
      ))}
    </div>
  );
}

function TodoUpdatePillItem({ pill, t }: { pill: Pill; t: (key: TKey) => string }) {
  // Sprint messages-section-fixes (D-msg-15) — explicit fallback for
  // unknown pill kinds. The previous `META[pill.kind] ?? META.completed!`
  // silently mis-rendered any future kind (e.g. a backend addition like
  // `'reopened'`) as "completed", which would lie to the founder. Render
  // nothing instead — the pill will reappear once the new kind is added
  // to META.
  const meta = META[pill.kind];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-caption px-2 py-0.5 rounded-full bg-accent border ${meta.border} ${meta.text}`}
      data-testid="todo-update-pill"
      data-kind={pill.kind}
    >
      <Icon name={meta.icon} size={10} className={meta.iconColor} aria-hidden="true" />
      <span className="font-medium">{t(meta.verbKey)}:</span>
      <span className="truncate max-w-todo-pill">{pill.headline}</span>
    </span>
  );
}

const META: Record<string, { icon: IconName; verbKey: TKey; text: string; iconColor: string; border: string }> = {
  started: {
    icon: 'Play',
    verbKey: 'todoUpdate.started',
    text: 'text-muted-foreground',
    iconColor: 'text-status-success',
    border: 'border-status-success/30',
  },
  completed: {
    icon: 'Check',
    verbKey: 'todoUpdate.completed',
    text: 'text-muted-foreground',
    iconColor: 'text-status-success',
    border: 'border-status-success/30',
  },
  cancelled: {
    icon: 'X',
    verbKey: 'todoUpdate.cancelled',
    text: 'text-muted-foreground/45',
    iconColor: 'text-muted-foreground/45',
    border: 'border-border/50',
  },
};
